import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { FC, useEffect, useState } from "react";

import {
  ArrowForward,
  Check,
  Error as ErrorIcon,
  Info,
  OneColored,
  Refresh,
  ThumbDownFilled,
  ThumbUpFilled,
} from "@livechat/design-system-icons";
import {
  Button,
  DesignToken,
  Heading,
  Icon,
  RadiusToken,
  SpacingToken,
  Tag,
  Text,
  Tooltip,
} from "@livechat/design-system-react-components";

import Carousel from "components/carousel/Carousel";
import Flex from "components/layout/Flex";
import { REPLY_SUGGESTIONS_FEEDBACK_URL } from "constants/feedback";
import { HelpCenter } from "constants/help-center";
import { ResponsiveBreakpoint } from "constants/responsive-breakpoint";
import { useBreakpoints } from "hooks/use-breakpoints";
import { ISuggestedPrompt } from "interfaces/entities/ticket";
import { ReplySuggestionRating } from "services/api/ticket/interfaces";
import { withVar } from "themes/helpers";

import { Stage, useReplySuggestions } from "../../../_hooks/use-reply-suggestions";

import {
  trackGenerateReplyClicked,
  trackLearnMoreClicked,
  trackLeaveFeedbackClicked,
  trackPreviewRepliesClicked,
  trackRating,
  trackRegenerateReplyClicked,
  trackRegenerateSuggestionsClicked,
  trackReportSuggestionsClicked,
} from "./analytics";

interface IReplySuggestionsProps {
  insertSignature: (preserveSelection?: boolean) => void;
  onReplySuggestionUse: (replySuggestionId: string) => void;
}

export const ReplySuggestions: FC<IReplySuggestionsProps> = ({ insertSignature, onReplySuggestionUse }) => {
  const {
    replyPath,
    generatePrompts,
    isLoadingPrompts,
    isLoadingReply,
    generateAndApplySuggestion,
    reportPoorPrompts,
    error,
    isReportingPoorPrompts,
    hasReportedPoorPrompts,
    rateSuggestion,
    appliedSuggestionRating,
    setHasClickedPreviewReplies,
    stage,
    failedSuggestionIndex,
    resetToChooseSuggestion,
    appliedPrompt,
  } = useReplySuggestions(insertSignature, onReplySuggestionUse);

  const handleRetry = (): void => {
    if (error === "prompts") {
      generatePrompts();
      trackRegenerateSuggestionsClicked();
    } else if (error === "reply") {
      resetToChooseSuggestion();
      if (appliedPrompt && failedSuggestionIndex !== undefined) {
        generateAndApplySuggestion(appliedPrompt, failedSuggestionIndex);
      }
      trackRegenerateReplyClicked();
    }
  };

  const handlePreviewRepliesClick = (): void => {
    setHasClickedPreviewReplies(true);
    trackPreviewRepliesClicked();
    generatePrompts();
  };

  switch (stage) {
    case Stage.Initial:
      return <InitialBanner generatePrompts={handlePreviewRepliesClick} isLoading={isLoadingPrompts} />;
    case Stage.ChooseSuggestion:
      if (!replyPath) {
        // unreachable as the ChooseSuggestion stage is based on the truthiness of replyPath
        return null;
      }
      return (
        <SuggestionCarousel
          suggestions={replyPath.suggestedPaths}
          handlePromptSelection={generateAndApplySuggestion}
          handlePoorPromptsReport={reportPoorPrompts}
          isLoadingReply={isLoadingReply}
          isReportingPoorPrompts={isReportingPoorPrompts}
          hasReportedPoorPrompts={hasReportedPoorPrompts}
          failedSuggestionIndex={failedSuggestionIndex}
        />
      );
    case Stage.ReplyApplied:
      return <SuggestionApplied rateSuggestion={rateSuggestion} appliedSuggestionRating={appliedSuggestionRating} />;
    case Stage.Error:
      if (!error) {
        // unreachable as the Error stage is based on the truthiness of error
        return null;
      }
      if (error === "no-prompts") {
        return <NoPromptsBanner />;
      }

      return <ErrorBanner error={error} handleRetry={handleRetry} />;
    default:
      return null;
  }
};

interface ISuggestionCarouselProps {
  suggestions: ISuggestedPrompt[];
  handlePromptSelection: (suggestion: ISuggestedPrompt, suggestionIndex?: number) => void;
  handlePoorPromptsReport: () => void;
  isLoadingReply: boolean;
  isReportingPoorPrompts: boolean;
  hasReportedPoorPrompts: boolean;
  failedSuggestionIndex?: number;
}

const ErrorBanner: FC<{ error: "prompts" | "reply"; handleRetry: () => void }> = ({ error, handleRetry }) => {
  return (
    <Banner>
      <Icon source={ErrorIcon} customColor={withVar(DesignToken.ContentBasicNegative)} />
      <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)} css={{ marginRight: "auto" }}>
        Reply suggestion{error === "prompts" ? "s" : ""} failed to generate
      </Text>
      <Button icon={<Icon source={Refresh} />} size="xcompact" kind="link" onClick={handleRetry}>
        Regenerate
      </Button>
    </Banner>
  );
};

const NoPromptsBanner: FC = () => {
  return (
    <Banner>
      <Icon source={Info} customColor={withVar(DesignToken.ContentBasicSecondary)} />
      <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)} css={{ marginRight: "auto" }}>
        No reply suggestions found
      </Text>
    </Banner>
  );
};

const InitialBanner: FC<{ generatePrompts: () => void; isLoading: boolean }> = ({ generatePrompts, isLoading }) => {
  const handleLearnMoreClick = (): void => {
    trackLearnMoreClicked();
  };

  const { isDesktop } = useBreakpoints();
  return (
    <OneBanner>
      <Icon source={OneColored} />
      {isDesktop && (
        <AITag size="small">
          <AITagText bold>Copilot</AITagText>
        </AITag>
      )}
      <Text size="sm" css={{ marginRight: "auto" }}>
        {isLoading ? "Generating reply suggestions" : "Respond to tickets quicker with smart reply suggestions"}
      </Text>
      <Button size="compact" kind="text" loading={isLoading} onClick={generatePrompts}>
        Preview replies
      </Button>
      {!isLoading && (
        <Tooltip
          kind="invert"
          triggerRenderer={() => <Icon source={Info} size="small" />}
          placement="top"
          triggerOnClick
          css={{
            width: "320px",
          }}
        >
          <Flex column gap={withVar(SpacingToken.Spacing3)}>
            <Heading size="xs">Get reply suggestions for your ticket</Heading>
            <Text>
              Text Copilot suggests replies based on resolved tickets with similar content. Respond smartly and
              accurately, every time.
            </Text>
            <Flex gap={withVar(SpacingToken.Spacing4)}>
              <Button kind="secondary" onClick={generatePrompts}>
                Try it
              </Button>
              <Button
                kind="link-inverted"
                onClick={handleLearnMoreClick}
                href={HelpCenter.REPLY_SUGGESTIONS}
                target="_blank"
              >
                Learn more
              </Button>
            </Flex>
          </Flex>
        </Tooltip>
      )}
    </OneBanner>
  );
};

const SUGGESTION_FADE_IN_DURATION = 300; // ms

const SuggestionCarousel: FC<ISuggestionCarouselProps> = ({
  suggestions,
  handlePromptSelection,
  handlePoorPromptsReport,
  isReportingPoorPrompts,
  hasReportedPoorPrompts,
  isLoadingReply,
  failedSuggestionIndex,
}) => {
  const [expandedItemIndex, setExpandedItemIndex] = useState<number | undefined>(failedSuggestionIndex);

  useEffect(() => {
    setExpandedItemIndex(failedSuggestionIndex);
  }, [failedSuggestionIndex]);

  const handleSuggestionClick = (index: number): void => {
    setExpandedItemIndex(index);
    handlePromptSelection(suggestions[index], index);
    trackGenerateReplyClicked(index);
  };

  const handleReport = (): void => {
    handlePoorPromptsReport();
    trackReportSuggestionsClicked();
  };

  return (
    <Carousel expandedItemIndex={expandedItemIndex}>
      {suggestions.map((suggestion, index) => (
        <Suggestion
          key={suggestion.pathID}
          suggestionText={suggestion.text}
          onClick={() => handleSuggestionClick(index)}
          enterDelay={index * SUGGESTION_FADE_IN_DURATION}
          isLoading={isLoadingReply}
          selected={index === expandedItemIndex}
        />
      ))}
      <NoSuggestionFit
        enterDelay={suggestions.length * SUGGESTION_FADE_IN_DURATION}
        onReport={handleReport}
        isReporting={isReportingPoorPrompts}
        hasReported={hasReportedPoorPrompts}
      />
    </Carousel>
  );
};

interface ISuggestionProps {
  suggestionText: string;
  onClick: () => void;
  enterDelay: number;
  selected: boolean;
  isLoading: boolean;
}

const Suggestion: FC<ISuggestionProps> = ({ suggestionText, onClick, enterDelay, selected, isLoading }) => {
  return (
    <InteractiveSuggestionWrapper
      onClick={isLoading ? undefined : onClick}
      enterDelay={enterDelay}
      css={selected && selectedSuggestionStyles}
      isLoading={isLoading}
    >
      <Text
        size="sm"
        css={{
          WebkitLineClamp: 2,
          color: withVar(DesignToken.ContentBasicPrimary),
        }}
      >
        {suggestionText}
      </Text>
      <Icon source={ArrowForward} customColor={withVar(DesignToken.ContentBasicPrimary)} />
      <Tooltip
        triggerRenderer={
          <Button
            size="xcompact"
            kind="secondary"
            css={{
              boxShadow: `-10px 0px 10px 0px ${withVar(DesignToken.SurfacePrimaryDefault)}`,
            }}
            loading={isLoading}
          >
            Generate reply
          </Button>
        }
        kind="invert"
        placement="top"
        hoverOnDelay={1000}
        hoverOffDuration={1}
        hoverOffDelay={1}
        portal
      >
        <Text
          css={{
            textAlign: "center",
            maxWidth: "220px",
          }}
        >
          The generated reply suggestion will be added to the message area below.
        </Text>
      </Tooltip>
    </InteractiveSuggestionWrapper>
  );
};

const NoSuggestionFit: FC<{ enterDelay: number; onReport: () => void; isReporting: boolean; hasReported: boolean }> = ({
  enterDelay,
  onReport,
  isReporting,
  hasReported,
}) => {
  return (
    <NonInteractiveSuggestionWrapper enterDelay={enterDelay}>
      <Text size="sm">None of the reply suggestions fit?</Text>

      <Button
        size="xcompact"
        kind="secondary"
        css={css(
          {
            right: withVar(SpacingToken.Spacing2),
            position: "absolute",
          },
          hasReported && inactiveButtonStyles
        )}
        loading={isReporting}
        onClick={hasReported ? undefined : onReport}
        icon={
          hasReported ? <Icon source={Check} customColor={withVar(DesignToken.ActionPositiveDefault)} /> : undefined
        }
      >
        {hasReported ? "Reported" : "Report"}
      </Button>
    </NonInteractiveSuggestionWrapper>
  );
};

const SuggestionApplied: FC<{
  rateSuggestion: (rating: ReplySuggestionRating) => Promise<void>;
  appliedSuggestionRating: ReplySuggestionRating | undefined;
}> = ({ rateSuggestion, appliedSuggestionRating }) => {
  const ratedGood = appliedSuggestionRating === ReplySuggestionRating.Positive;
  const ratedBad = appliedSuggestionRating === ReplySuggestionRating.Negative;
  const rated = appliedSuggestionRating !== undefined;

  const handleRating = (rating: ReplySuggestionRating): void => {
    trackRating(rating === ReplySuggestionRating.Positive ? "good" : "bad");
    rateSuggestion(rating);
  };

  const handlePositiveRating = rated ? undefined : (): void => handleRating(ReplySuggestionRating.Positive);
  const handleNegativeRating = rated ? undefined : (): void => handleRating(ReplySuggestionRating.Negative);

  const ratingMessage = rated ? (ratedGood ? "Rated Good" : "Rated Bad") : "Rate reply";

  return (
    <Banner
      css={{
        animation: `${fadeIn} 0.2s ease-out forwards`,
      }}
    >
      <Icon source={Check} customColor={withVar(DesignToken.ActionPositiveDefault)} />
      <Text size="sm" customColor={withVar(DesignToken.ContentBasicSecondary)} css={{ marginRight: "auto" }}>
        Reply suggestion added to the message area
      </Text>

      <RatingContainer>
        <Text size="xs">{ratingMessage}</Text>

        <Flex alignItems="center">
          <RatingButton visible={!ratedBad} otherClosed={ratedGood}>
            <Button
              size="xcompact"
              kind="secondary"
              css={css(
                {
                  borderRadius: "50%",
                },
                rated && inactiveButtonStyles
              )}
              icon={
                <Icon
                  kind="action-neutral"
                  source={ThumbUpFilled}
                  customColor={ratedGood ? withVar(DesignToken.DecorPurple300) : undefined}
                />
              }
              onClick={handlePositiveRating}
            />
          </RatingButton>

          <RatingButton visible={!ratedGood} otherClosed={ratedBad}>
            <Button
              size="xcompact"
              kind="secondary"
              css={css(
                {
                  borderRadius: "50%",
                },
                rated && inactiveButtonStyles
              )}
              icon={
                <Icon
                  kind="action-neutral"
                  source={ThumbDownFilled}
                  customColor={ratedBad ? withVar(DesignToken.DecorPurple300) : undefined}
                />
              }
              onClick={handleNegativeRating}
            />
          </RatingButton>
        </Flex>
      </RatingContainer>
      <Button
        href={REPLY_SUGGESTIONS_FEEDBACK_URL}
        size="xcompact"
        kind="link-light"
        target="_blank"
        onClick={() => {
          trackLeaveFeedbackClicked();
        }}
      >
        <Text size="xs">Leave feedback</Text>
      </Button>
    </Banner>
  );
};

const RatingContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${withVar(SpacingToken.Spacing2)};
`;

const RatingButton = styled.div<{ visible: boolean; otherClosed: boolean }>`
  transition: all 0.2s ease-in-out;
  opacity: ${({ visible }): number => (visible ? 1 : 0)};
  width: ${({ visible }): string => (visible ? "24px" : "0")};
  margin-right: ${({ visible }): string => (visible ? withVar(SpacingToken.Spacing2) : "0")};
  margin-right: ${({ otherClosed }): string | undefined => (otherClosed ? "0" : undefined)};

  &:last-child {
    margin-right: 0;
  }
`;

const fadeIn = keyframes`
from{
  opacity: 0;
  translate: 0 20px;
}
to{
  opacity: 1;
  translate: 0 0;
}
`;

const BaseSuggestionWrapper = styled.div<{ enterDelay: number }>`
  box-sizing: border-box;
  height: 56px;
  width: 320px;
  position: relative;
  display: flex;
  align-items: center;
  text-align: left;
  overflow: clip visible;
  justify-content: space-between;

  background: ${withVar(DesignToken.SurfacePrimaryDefault)};
  padding-block: ${withVar(SpacingToken.Spacing2)};
  padding-inline: ${withVar(SpacingToken.Spacing3)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};

  opacity: 0;
  translate: 0 10px;

  animation: ${fadeIn} 0.3s ${({ enterDelay }): number => enterDelay}ms ease-out forwards;
`;

const InteractiveSuggestionWrapper = styled(BaseSuggestionWrapper.withComponent("button"))<{ isLoading: boolean }>`
  & > div {
    transition: right 0.2s ease-in-out;
    position: absolute;
    right: -40%;

    right: ${({ isLoading }): string => (isLoading ? withVar(SpacingToken.Spacing2) : "-40%")};
  }

  & > div:focus-within {
    right: ${withVar(SpacingToken.Spacing2)};
  }

  &:hover > div {
    right: ${withVar(SpacingToken.Spacing2)};
  }
`;

const selectedSuggestionStyles = css`
  transition: width 0.2s ease-in-out;
  width: 100%;
`;

const NonInteractiveSuggestionWrapper = styled(BaseSuggestionWrapper)`
  background: ${withVar(DesignToken.SurfaceSecondaryDefault)};
`;

const Banner = styled.div`
  box-sizing: border-box;
  height: 56px;
  padding-inline: ${withVar(SpacingToken.Spacing3)};
  padding-block: ${withVar(SpacingToken.Spacing4)};
  border: 1px solid ${withVar(DesignToken.BorderBasicSecondary)};
  border-radius: ${withVar(RadiusToken.Radius3)};
  gap: ${withVar(SpacingToken.Spacing2)};
  display: flex;
  align-items: center;
  background-color: ${withVar(DesignToken.SurfacePrimaryDefault)};

  @media (max-width: ${ResponsiveBreakpoint.Mobile}px) {
    margin-inline: ${withVar(SpacingToken.Spacing2)};
  }
`;

const OneBanner = styled(Banner)`
  background: ${withVar(DesignToken.SurfaceOtherBot)};
  box-shadow: none;
`;

const AITag = styled(Tag)`
  background: ${withVar(DesignToken.SurfaceGradient10)};
`;

const AITagText = styled(Text)`
  background: ${withVar(DesignToken.ContentBasicGradient01)};
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  pointer-events: none;
`;

const inactiveButtonStyles = css`
  pointer-events: none;
`;
